import type { ComponentPropsWithoutRef, PropsWithChildren } from "react";
import type { Components } from "react-markdown";

import {
  Markdown,
  MarkdownContextProvider,
} from "@alaffia-technology-solutions/components";
import { Button } from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { ChevronLeft, ChevronRight, Save, SquarePen } from "lucide-react";

import { EditIndicator } from "./EditIndicator";
import { EditReasonForm } from "./EditReasonForm";
import { MessageContent } from "./MessageContent";
import { StatusChangeAlert } from "./StatusChangeAlert";
import { RejectForm, UserValidationButton } from "./UserValidationButtons";
import { UserValidationStatus } from "./UserValidationStatus";

function BaseAgentView({ children }: PropsWithChildren) {
  return (
    <div className="af-relative af-flex af-h-full af-flex-col af-justify-between af-overflow-hidden">
      {children}
    </div>
  );
}

interface BodyProps extends PropsWithChildren {
  editMode?: boolean;
}

function Body({ editMode, children }: BodyProps) {
  return (
    <div className="af-h-full af-overflow-y-auto">
      <div
        className={cn(
          "af-m-4 af-mb-24 af-flex af-flex-col af-gap-4 af-rounded-lg af-bg-secondary/20 af-px-4 af-py-5",
          {
            "af-border-primary/50 af-bg-secondary/10 af-shadow-md": editMode,
          },
        )}
      >
        {children}
      </div>
    </div>
  );
}

interface HeaderProps extends PropsWithChildren {
  markdownTitle?: string | null;
}

function Header({ markdownTitle, children }: HeaderProps) {
  if (!markdownTitle) {
    return null;
  }

  return (
    <div className="af-flex af-flex-col af-gap-2">
      {markdownTitle && (
        <MarkdownContextProvider value={markdownContentContext}>
          <Markdown>{markdownTitle}</Markdown>
        </MarkdownContextProvider>
      )}
      <div className="af-flex af-gap-2">{children}</div>
    </div>
  );
}

const H2: Components["h2"] = ({ children }) => (
  <h2 className="af-text-base af-font-medium">{children}</h2>
);

const markdownComponents = { h2: H2 };
const markdownContentContext = { components: markdownComponents };

function Footer({ children }: PropsWithChildren) {
  return (
    <div className="af-absolute af-bottom-4 af-left-4 af-right-4 af-flex af-gap-2 af-overflow-auto af-rounded-lg af-border af-border-input af-bg-card af-p-3 af-shadow-lg">
      {children}
    </div>
  );
}

type ButtonProps = ComponentPropsWithoutRef<typeof Button>;

function PreviousButton(props: ButtonProps) {
  return (
    <Button size="icon" variant="outline" {...props}>
      <ChevronLeft className="af-size-4" />
    </Button>
  );
}

function NextButton({ className, ...props }: ButtonProps) {
  return (
    <Button
      size="icon"
      variant="outline"
      className={cn("af-mr-auto", className)}
      {...props}
    >
      <ChevronRight className="af-size-4" />
    </Button>
  );
}

function EditButton(props: ButtonProps) {
  return (
    <Button variant="outline" {...props}>
      <SquarePen className="af-size-4" />
      Edit
    </Button>
  );
}

function CancelButton(props: ButtonProps) {
  return (
    <Button variant="outline" {...props}>
      Cancel
    </Button>
  );
}

function SaveChangesButton(props: ButtonProps) {
  return (
    <Button {...props}>
      <Save className="af-size-4" />
      Save Changes
    </Button>
  );
}

export const AgentView = Object.assign(BaseAgentView, {
  Body,
  Header,
  MessageContent,
  Footer,
  PreviousButton,
  NextButton,
  EditButton,
  EditReasonForm,
  EditIndicator,
  StatusChangeAlert,
  RejectForm,
  CancelButton,
  SaveChangesButton,
  UserValidationButton,
  UserValidationStatus,
});
